/**
 * Microphone capture for a hands-free voice session.
 *
 * Differs from the push-to-talk recorder in three ways that matter once the
 * loop re-arms itself:
 *
 * 1. One `getUserMedia` stream is held for the whole session. Re-acquiring per
 *    utterance costs hundreds of milliseconds and re-prompts on some browsers.
 * 2. The speech threshold is derived from a measured noise floor rather than a
 *    fixed level, so a noisy room does not read as continuous speech (which
 *    would never endpoint) and a quiet one does not swallow soft speech.
 * 3. It can watch for speech *while Tally is talking* so the user can interrupt.
 */

/** Time spent sampling the room before the first utterance. */
const CALIBRATION_MS = 400;
/** Level-metering cadence. */
const POLL_MS = 50;
/** Silence after speech onset that ends an utterance. */
const DEFAULT_SILENCE_HANG_MS = 1400;
/** Armed with nobody speaking - gives up so the session can wind down. */
const DEFAULT_NO_SPEECH_TIMEOUT_MS = 7000;
/** Hard cap on one utterance. */
const DEFAULT_MAX_UTTERANCE_MS = 25000;
/** Sustained speech required to interrupt playback. */
const BARGE_IN_SUSTAIN_MS = 400;
/**
 * Speech dips below the threshold between syllables. Only a quiet stretch this
 * long ends an interruption attempt; without it a spoken "stop" rarely held
 * above the threshold for the full sustain window.
 */
const BARGE_IN_GAP_MS = 200;
/** Speech is this much above the measured noise floor. */
const SPEECH_FACTOR = 3.5;
/**
 * Interrupting takes a higher bar: echo cancellation is imperfect, so Tally's
 * own voice leaks into the microphone and must not read as the user speaking.
 */
const BARGE_IN_FACTOR = 6;
/** Floor under the adaptive threshold, for rooms quiet enough to measure ~0. */
const MIN_SPEECH_LEVEL = 0.012;
/** Utterances shorter than this are a cough or a door, not a question. */
const MIN_UTTERANCE_MS = 350;

export interface VoiceCaptureCallbacks {
  /** An endpointed utterance, ready to transcribe. */
  onUtterance: (audio: Blob) => void;
  /** Armed, but nobody spoke before the timeout. */
  onNoSpeech: () => void;
  /** Sustained speech detected while watching during playback. */
  onBargeIn: () => void;
  /** Speech began while watching - it may or may not become a barge-in. */
  onSpeechStart?: () => void;
  /** That speech stopped before it became a barge-in. */
  onSpeechEnd?: () => void;
  /** Current input level, 0..1, for the overlay animation. */
  onLevel?: (level: number) => void;
  onError: (error: Error) => void;
}

export interface VoiceCaptureOptions {
  silenceHangMs?: number;
  noSpeechTimeoutMs?: number;
  maxUtteranceMs?: number;
}

type CaptureMode = "idle" | "armed" | "watching";

export class VoiceCapture {
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  private mode: CaptureMode = "idle";
  private noiseFloor = 0;
  private calibrating = true;
  private calibrationSamples: number[] = [];

  private speechStartedAt: number | null = null;
  private silenceStartedAt: number | null = null;
  private armedAt = 0;
  private bargeInStartedAt: number | null = null;
  private bargeInQuietSince: number | null = null;

  private readonly silenceHangMs: number;
  private readonly noSpeechTimeoutMs: number;
  private readonly maxUtteranceMs: number;

  constructor(
    private readonly callbacks: VoiceCaptureCallbacks,
    options: VoiceCaptureOptions = {},
  ) {
    this.silenceHangMs = options.silenceHangMs ?? DEFAULT_SILENCE_HANG_MS;
    this.noSpeechTimeoutMs =
      options.noSpeechTimeoutMs ?? DEFAULT_NO_SPEECH_TIMEOUT_MS;
    this.maxUtteranceMs = options.maxUtteranceMs ?? DEFAULT_MAX_UTTERANCE_MS;
  }

  /** Acquires the microphone and measures the room. Call once per session. */
  async open(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Without echo cancellation the session hears itself and barge-in
        // triggers on Tally's own voice.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    const audioContext = new AudioContext();
    // Browsers hand back a suspended context when it was not created directly
    // inside the gesture; resuming is a no-op when it is already running.
    if (audioContext.state === "suspended") {
      await audioContext.resume().catch(() => {});
    }

    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 2048;
    const source = audioContext.createMediaStreamSource(this.stream);
    source.connect(analyser);

    this.audioContext = audioContext;
    this.analyser = analyser;
    this.source = source;
    this.calibrating = true;
    this.calibrationSamples = [];

    this.pollTimer = setInterval(() => this.poll(), POLL_MS);
  }

  /** Starts recording and endpoints on silence. */
  arm(): void {
    if (!this.stream || this.mode === "armed") return;

    this.chunks = [];
    try {
      this.recorder = new MediaRecorder(this.stream);
    } catch (error) {
      this.callbacks.onError(
        error instanceof Error ? error : new Error("Recorder failed to start"),
      );
      return;
    }

    this.recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    this.recorder.onstop = () => this.emitUtterance();

    this.recorder.start();
    this.mode = "armed";
    this.armedAt = Date.now();
    this.speechStartedAt = null;
    this.silenceStartedAt = null;
  }

  /** Stops recording without emitting - used when the session is torn down. */
  disarm(): void {
    if (this.recorder && this.recorder.state !== "inactive") {
      this.recorder.onstop = null;
      try {
        this.recorder.stop();
      } catch {
        // Already stopping.
      }
    }
    this.recorder = null;
    this.chunks = [];
    this.mode = "idle";
    this.speechStartedAt = null;
    this.silenceStartedAt = null;
  }

  /** Listens for the user talking over Tally, without recording. */
  watchForBargeIn(): void {
    this.disarm();
    this.mode = "watching";
    this.bargeInStartedAt = null;
    this.bargeInQuietSince = null;
  }

  /** Releases the microphone and every audio node. */
  close(): void {
    this.disarm();
    this.mode = "idle";

    if (this.pollTimer !== null) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.source?.disconnect();
    this.source = null;
    this.analyser = null;
    this.audioContext?.close().catch(() => {});
    this.audioContext = null;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  private currentLevel(): number {
    if (!this.analyser) return 0;
    const data = new Uint8Array(this.analyser.fftSize);
    this.analyser.getByteTimeDomainData(data);

    let sum = 0;
    for (let i = 0; i < data.length; i++) {
      const value = (data[i] - 128) / 128;
      sum += value * value;
    }
    return Math.sqrt(sum / data.length);
  }

  private speechThreshold(factor: number): number {
    return Math.max(this.noiseFloor * factor, MIN_SPEECH_LEVEL);
  }

  private poll(): void {
    const level = this.currentLevel();
    this.callbacks.onLevel?.(level);

    if (this.calibrating) {
      this.calibrationSamples.push(level);
      if (this.calibrationSamples.length * POLL_MS >= CALIBRATION_MS) {
        // Median, not mean: a cough during calibration should not set the floor.
        const sorted = [...this.calibrationSamples].sort((a, b) => a - b);
        this.noiseFloor = sorted[Math.floor(sorted.length / 2)] ?? 0;
        this.calibrating = false;
      }
      return;
    }

    switch (this.mode) {
      case "armed":
        this.pollArmed(level);
        break;
      case "watching":
        this.pollWatching(level);
        break;
      default:
        // Idle: keep tracking the room so the floor stays current between turns.
        this.trackNoiseFloor(level);
    }
  }

  /** Slow EMA over quiet samples, so the floor follows the room, not speech. */
  private trackNoiseFloor(level: number): void {
    if (level < this.speechThreshold(SPEECH_FACTOR)) {
      this.noiseFloor = this.noiseFloor * 0.95 + level * 0.05;
    }
  }

  private pollArmed(level: number): void {
    const now = Date.now();
    const threshold = this.speechThreshold(SPEECH_FACTOR);

    if (this.speechStartedAt === null) {
      if (level >= threshold) {
        this.speechStartedAt = now;
        this.silenceStartedAt = null;
        return;
      }
      this.trackNoiseFloor(level);
      if (now - this.armedAt >= this.noSpeechTimeoutMs) {
        this.disarm();
        this.callbacks.onNoSpeech();
      }
      return;
    }

    if (now - this.speechStartedAt >= this.maxUtteranceMs) {
      this.stopAndEmit();
      return;
    }

    if (level < threshold) {
      if (this.silenceStartedAt === null) {
        this.silenceStartedAt = now;
      } else if (now - this.silenceStartedAt >= this.silenceHangMs) {
        this.stopAndEmit();
      }
    } else {
      this.silenceStartedAt = null;
    }
  }

  private pollWatching(level: number): void {
    const now = Date.now();
    if (level >= this.speechThreshold(BARGE_IN_FACTOR)) {
      this.bargeInQuietSince = null;
      if (this.bargeInStartedAt === null) {
        this.bargeInStartedAt = now;
        this.callbacks.onSpeechStart?.();
      } else if (now - this.bargeInStartedAt >= BARGE_IN_SUSTAIN_MS) {
        this.bargeInStartedAt = null;
        this.mode = "idle";
        this.callbacks.onBargeIn();
      }
      return;
    }

    if (this.bargeInStartedAt === null) return;
    if (this.bargeInQuietSince === null) {
      this.bargeInQuietSince = now;
    } else if (now - this.bargeInQuietSince >= BARGE_IN_GAP_MS) {
      this.bargeInStartedAt = null;
      this.bargeInQuietSince = null;
      this.callbacks.onSpeechEnd?.();
    }
  }

  private stopAndEmit(): void {
    this.mode = "idle";
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop(); // onstop -> emitUtterance
        return;
      } catch {
        // Fall through and emit what we have.
      }
    }
    this.emitUtterance();
  }

  private emitUtterance(): void {
    const spokenMs =
      this.speechStartedAt === null ? 0 : Date.now() - this.speechStartedAt;
    const chunks = this.chunks;
    this.chunks = [];
    this.recorder = null;
    this.speechStartedAt = null;
    this.silenceStartedAt = null;

    if (chunks.length === 0 || spokenMs < MIN_UTTERANCE_MS) {
      // Too short to be a question - treat as if nobody spoke rather than
      // spending a transcription call on it.
      this.callbacks.onNoSpeech();
      return;
    }

    this.callbacks.onUtterance(new Blob(chunks, { type: "audio/webm" }));
  }
}
